import { MAX_DOCUMENT_BYTES, MAX_FILE_NAME, type DocumentMime } from '@gk/contracts'

/** Taking a picture on a phone (ARCHITECTURE 9.10): the camera sheet returns whatever the phone makes (12 MP, EXIF with the position of the shed). Re-encoding it on a canvas
 *  does three jobs at once: it fits the picture to a size a field network can carry, it drops every metadata block (no GPS, no device id: I14), and it fixes the
 *  orientation. The result is hashed with WebCrypto: the hash IS the file's name (a content-addressed file is stored once however often it is sent). */
export const MAX_SIDE = 1600
export const JPEG_QUALITY = 0.8

export interface PreparedPhoto { blob: Blob; sha256: string; bytes: number; mime: 'image/jpeg'; capturedAt: string }

export class PhotoUnreadableError extends Error { constructor() { super('PHOTO_UNREADABLE') } }

export async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export function fitWithin(width: number, height: number, max = MAX_SIDE): { width: number; height: number } {
  const longest = Math.max(width, height)
  if (longest <= max) return { width, height }
  const k = max / longest
  return { width: Math.max(1, Math.round(width * k)), height: Math.max(1, Math.round(height * k)) }
}

const toJpeg = (canvas: HTMLCanvasElement): Promise<Blob> => new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new PhotoUnreadableError())), 'image/jpeg', JPEG_QUALITY))

export async function prepareImage(file: Blob, now: Date = new Date(), maxSide = MAX_SIDE): Promise<PreparedPhoto> {
  let bitmap: ImageBitmap
  try { bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' }) } catch { throw new PhotoUnreadableError() }
  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height, maxSide)
    const canvas = document.createElement('canvas')
    canvas.width = width; canvas.height = height
    const g = canvas.getContext('2d')
    if (!g) throw new PhotoUnreadableError()
    g.fillStyle = '#fff'; g.fillRect(0, 0, width, height)             // a transparent PNG would turn black as a JPEG
    g.drawImage(bitmap, 0, 0, width, height)
    const blob = await toJpeg(canvas)
    return { blob, sha256: await sha256Hex(blob), bytes: blob.size, mime: 'image/jpeg', capturedAt: now.toISOString() }
  } finally { bitmap.close() }
}

// ---------------------------------------------------------------------------------------------------------- M15-V: a document
/** A scanned label or sheet keeps more of its small print than a photo of the course: its longest side is fitted within this. */
export const DOCUMENT_MAX_SIDE = 2400
export interface PreparedDocument { blob: Blob; sha256: string; bytes: number; mime: DocumentMime; capturedAt: string; name: string }
/** Not a PDF nor an image the app can read. */
export class DocumentTypeError extends Error { constructor() { super('DOCUMENT_TYPE') } }
/** A PDF over the documents bucket's limit (20 MB). */
export class DocumentTooLargeError extends Error { constructor() { super('DOCUMENT_TOO_LARGE') } }

const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46, 0x2d]                          // "%PDF-": what the file service checks too
/** The name kept for the list (the link's caption): the file's own name without any folder, at most 200 code points, or a plain default. */
export function documentName(name: string | undefined, mime: string): string {
  const base = (name ?? '').split(/[\\/]/).pop()?.trim() ?? ''
  const cut = [...base].slice(0, MAX_FILE_NAME).join('')
  return cut || (mime === 'application/pdf' ? 'document.pdf' : 'document.jpg')
}
/** A document picked on the phone or the office's disk (M15-V, the vault). A PDF is kept AS IT IS — its bytes are the document, never re-encoded — once its
 *  first bytes say it is one and it fits the documents bucket; an image goes through `prepareImage` (fitted within 2400 px, every metadata block dropped:
 *  no position, I14). The hash names it, as a photo. Nothing here reads the network. */
export async function prepareDocument(file: Blob & { name?: string }, now: Date = new Date()): Promise<PreparedDocument> {
  const head = new Uint8Array(await file.slice(0, PDF_MAGIC.length).arrayBuffer())
  const isPdf = head.length === PDF_MAGIC.length && PDF_MAGIC.every((b, i) => head[i] === b)
  if (isPdf) {
    if (file.size > MAX_DOCUMENT_BYTES) throw new DocumentTooLargeError()
    const blob = file.type === 'application/pdf' ? file : new Blob([file], { type: 'application/pdf' })
    return { blob, sha256: await sha256Hex(blob), bytes: blob.size, mime: 'application/pdf', capturedAt: now.toISOString(), name: documentName(file.name, 'application/pdf') }
  }
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name ?? '')) throw new DocumentTypeError()      // says PDF, is not one
  if (!file.type.startsWith('image/')) throw new DocumentTypeError()
  let image: PreparedPhoto
  try { image = await prepareImage(file, now, DOCUMENT_MAX_SIDE) } catch { throw new DocumentTypeError() }
  return { ...image, name: documentName(file.name?.replace(/\.[a-z0-9]{1,5}$/i, '.jpg'), 'image/jpeg') }
}
